<script setup lang="ts">
import { toRefs } from 'vue';

interface LoaderProps {
  size?: number;
  relative?: boolean;
  fullPage?: boolean;
}

const props = withDefaults(defineProps<LoaderProps>(), {
  size: 96,
  relative: false,
  fullPage: false,
});

const { size, relative, fullPage } = toRefs(props);
const viewBoxSize = `0 0 ${size.value} ${size.value}`;
</script>

<template>
  <div :class="{ 'full-page-loader': fullPage }">
    <svg
      :width="size"
      :height="size"
      :viewBox="viewBoxSize"
      fill="none"
      class="z-5"
      :class="{ 'absolute m-auto left-0 right-0 bottom-0 top-0': !relative }"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="burst" fill="#078888">
        <g>
          <rect class="ray-2" x="46" y="2" width="4" height="12" rx="2" />
          <rect class="ray-1" x="46" y="18" width="4" height="12" rx="2" />
          <rect class="ray-9" x="46" y="66" width="4" height="16" rx="2" />
        </g>
        <g>
          <rect
            class="ray-2"
            x="57.6327"
            y="19.5182"
            width="4"
            height="12"
            rx="2"
            transform="rotate(22.5 57.6327 19.5182)"
          />
          <rect
            class="ray-10"
            x="39.2639"
            y="63.8645"
            width="4"
            height="12"
            rx="2"
            transform="rotate(22.5 39.2639 63.8645)"
          />
          <rect
            class="ray-11"
            x="33.141"
            y="78.6465"
            width="4"
            height="12"
            rx="2"
            transform="rotate(22.5 33.141 78.6465)"
          />
        </g>
        <g>
          <rect
            class="ray-4"
            x="79.1127"
            y="14.0589"
            width="4"
            height="12"
            rx="2"
            transform="rotate(45 79.1127 14.0589)"
          />
          <rect
            class="ray-3"
            x="67.799"
            y="25.3726"
            width="4"
            height="12"
            rx="2"
            transform="rotate(45 67.799 25.3726)"
          />
          <rect
            class="ray-11"
            x="33.8579"
            y="59.3137"
            width="4"
            height="16"
            rx="2"
            transform="rotate(45 33.8579 59.3137)"
          />
        </g>
        <g>
          <rect
            class="ray-5"
            x="87.8827"
            y="29.3153"
            width="4"
            height="15.9971"
            rx="2"
            transform="rotate(67.5 87.8827 29.3153)"
          />
          <rect
            class="ray-4"
            x="69.4077"
            y="36.9678"
            width="4"
            height="6"
            rx="2"
            transform="rotate(67.5 69.4077 36.9678)"
          />
          <rect class="ray-12" x="30.4819" y="53" width="4" height="20" rx="2" transform="rotate(67.5 30.4819 53)" />
        </g>
        <g>
          <rect class="ray-14" x="4" y="50" width="4" height="16" rx="2" transform="rotate(-90 4 50)" />
          <rect class="ray-13" x="24" y="50" width="4" height="6" rx="2" transform="rotate(-90 24 50)" />
          <rect class="ray-5" x="66" y="50" width="4" height="16" rx="2" transform="rotate(-90 66 50)" />
        </g>
        <g>
          <rect
            class="ray-7"
            x="91.2638"
            y="63.7557"
            width="4"
            height="12"
            rx="2"
            transform="rotate(112.5 91.2638 63.7557)"
          />
          <rect
            class="ray-6"
            x="76.4818"
            y="57.6327"
            width="4"
            height="12"
            rx="2"
            transform="rotate(112.5 76.4818 57.6327)"
          />
          <rect
            class="ray-14"
            x="32.1355"
            y="39.2639"
            width="4"
            height="16"
            rx="2"
            transform="rotate(112.5 32.1355 39.2639)"
          />
        </g>
        <g>
          <rect
            class="ray-15"
            x="29.6152"
            y="32.4437"
            width="4"
            height="6"
            rx="2"
            transform="rotate(-45 29.6152 32.4437)"
          />
          <rect
            class="ray-16"
            x="22.5442"
            y="25.3726"
            width="4"
            height="6"
            rx="2"
            transform="rotate(-45 22.5442 25.3726)"
          />
          <rect
            class="ray-1"
            x="15.4731"
            y="18.3015"
            width="4"
            height="6"
            rx="2"
            transform="rotate(-45 15.4731 18.3015)"
          />
          <rect
            class="ray-7"
            x="59.3137"
            y="62.1421"
            width="4"
            height="16"
            rx="2"
            transform="rotate(-45 59.3137 62.1421)"
          />
        </g>
        <g>
          <rect
            class="ray-8"
            x="59.0322"
            y="69.4077"
            width="4"
            height="6"
            rx="2"
            transform="rotate(157.5 59.0322 69.4077)"
          />
          <rect
            class="ray-9"
            x="62.859"
            y="78.6465"
            width="4"
            height="6"
            rx="2"
            transform="rotate(157.5 62.859 78.6465)"
          />
          <rect
            class="ray-10"
            x="66.6858"
            y="87.8853"
            width="4"
            height="6"
            rx="2"
            transform="rotate(157.5 66.6858 87.8853)"
          />
          <rect
            class="ray-16"
            x="42.9595"
            y="30.6048"
            width="4"
            height="20"
            rx="2"
            transform="rotate(157.5 42.9595 30.6048)"
          />
        </g>
      </g>
    </svg>
  </div>
</template>

<style scoped lang="scss">
.full-page-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.5);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}
#burst rect {
  animation: burst-highlight 1.6s linear infinite;
  transition: 0.2s ease;
}
#burst rect.ray-1 {
  animation-delay: -1.5s;
}
#burst rect.ray-2 {
  animation-delay: -1.4s;
}
#burst rect.ray-3 {
  animation-delay: -1.3s;
}
#burst rect.ray-4 {
  animation-delay: -1.2s;
}
#burst rect.ray-4 {
  animation-delay: -1.2s;
}
#burst rect.ray-5 {
  animation-delay: -1.1s;
}
#burst rect.ray-6 {
  animation-delay: -1s;
}
#burst rect.ray-7 {
  animation-delay: -0.9s;
}
#burst rect.ray-8 {
  animation-delay: -0.8s;
}
#burst rect.ray-9 {
  animation-delay: -0.7s;
}
#burst rect.ray-10 {
  animation-delay: -0.6s;
}
#burst rect.ray-11 {
  animation-delay: -0.5s;
}
#burst rect.ray-12 {
  animation-delay: -0.4s;
}
#burst rect.ray-13 {
  animation-delay: -0.3s;
}
#burst rect.ray-14 {
  animation-delay: -0.2s;
}
#burst rect.ray-15 {
  animation-delay: -0.1s;
}
#burst rect.ray-16 {
  animation-delay: 0s;
}
@keyframes burst-highlight {
  0% {
    fill: #078888;
    opacity: 1;
  }
  60% {
    fill: #8bcfcf;
  }
  100% {
    opacity: 0;
  }
}
</style>
